import type { RefObject } from 'react';
import { useMemo } from 'react';

import { useChartContext } from '@/components/Chart/context/ChartContext';
import ExportButton from '@/components/Chart/ExportButton';
import type { ChartType, ExtendedSeriesOptionsType } from '@/components/Chart/types/highcharts';
import { useLightMode } from '@/context/LightModeContext';
import { Permission } from '@/entities/permissions';
import type { SymbolKey } from '@/entities/symbol';
import {
  addSymbols,
  requestSpotSelection,
  SELECTOR_MODES,
  setSelectorMode
} from '@/features/SymbolSelector/store/symbolSelector.slice';
import { FORWARD_CURVE_PERIOD_WILDCARD } from '@/features/SymbolSelector/utils/expandForwardCurveWildcards';
import { useAppDispatch } from '@/hooks/redux-hooks';
import { ExportSelectionSource } from '@/hooks/useOpenExportSelection';
import { useOptionalMarketWidget } from '@/pages/Markets/MarketsComponents/contexts/MarketWidgetContext';
import usePermission from '@/shared/hooks/usePermission';
import { openExportDrawer } from '@/slices/drawerSlice';
import { setExport } from '@/slices/exportSlice';

const getSelectedTimeRange = (chartRef: RefObject<any>): string | undefined => {
  if (!chartRef.current?.chart) return undefined;
  const chart = chartRef.current.chart;
  const index = chart.rangeSelector?.selected;
  if (index === undefined) return undefined;
  const buttonConfig = chart.rangeSelector.buttonOptions[index];
  if (!buttonConfig || buttonConfig.text === 'Max') return undefined;
  return buttonConfig.text;
};

const toSymbolKey =
  (defaultPeriod: string) =>
  (series: ExtendedSeriesOptionsType): SymbolKey => ({
    Code: series.custom.Code,
    Period: series.custom.Period || defaultPeriod,
    PeriodType: series.custom.PeriodType,
    TimeRef: series.custom.TimeRef,
    transactionDate: series.custom.transactionDate
  });

const toSeriesColor = (series: ExtendedSeriesOptionsType): string | undefined =>
  'color' in series ? (series.color as string | undefined) : undefined;

const ExportChartButton = ({
  disabled = false,
  graphType,
  metadata,
  tabName
}: {
  graphType: ChartType | 'fullscreen';
  disabled?: boolean;
  metadata?: Record<string, string>;
  tabName?: string;
}) => {
  const { hasPermission } = usePermission();
  const isDisabled = disabled || !hasPermission(Permission.ExportImage);

  const { chartSeries, chartRef } = useChartContext();
  const { isLightMode } = useLightMode();
  const { isFullScreen } = useOptionalMarketWidget();
  const dispatch = useAppDispatch();
  const type = useMemo(() => {
    const temp = graphType || 'spot';
    return temp === 'spot' && isFullScreen ? 'fullscreen' : temp;
  }, [graphType, isFullScreen]);

  const exportSelection = () => {
    if (!chartSeries) return;

    // Forward-curve chart series don't carry Period in their key (the widget
    // represents the whole curve). Use the wildcard so the selector-level
    // resolver can expand it to all periods via the forward catalog.
    const defaultPeriod = type === 'forwardSimple' ? FORWARD_CURVE_PERIOD_WILDCARD : '';
    const selectedSymbols = chartSeries.map(toSymbolKey(defaultPeriod));
    const selectedTimeRange = getSelectedTimeRange(chartRef);
    const colors = chartSeries.map(toSeriesColor).filter((c): c is string => Boolean(c));

    if (type === 'forwardSimple') {
      dispatch(setSelectorMode(SELECTOR_MODES.FORWARD_CURVE));
      dispatch(addSymbols({ symbols: selectedSymbols }));
    } else {
      dispatch(requestSpotSelection({ symbols: selectedSymbols }));
    }
    // setExport must be dispatched before openExportDrawer so that exportNameBase
    // is already in Redux state when the drawer mounts and reads it.
    dispatch(
      setExport({
        exportType: type,
        from: metadata?.from,
        isLightMode,
        selectedTimeRange,
        exportNameBase: tabName,
        seriesColors: colors.length ? colors : undefined
      })
    );
    dispatch(
      openExportDrawer({ showHistory: false, source: ExportSelectionSource.CHART_WIDGET_BUTTON })
    );
  };

  return (
    <ExportButton
      disabled={isDisabled}
      handleClick={exportSelection}
      title='Export chart'
    />
  );
};

export default ExportChartButton;
